import { useMemo } from "react"
import { useNavigate } from "react-router-dom"
import {
    MERCHANT_ADMIN_PRODUCTS_CREATE_ROUTE,
    MERCHANT_ADMIN_ROUTE,
} from "../../../../../constants/routes"
import { useAppLocalization } from "../../../../localization/components/AppLocalizationProvider"
import { PageHeader } from "../../../../../shared/components/pageHeader/PageHeader"
import { LocalizedStrict } from "../../../../localization/components/AppLocalized"
import { StandardTabs } from "../../../../../shared/components/tabs/StandardTabs"
import { Page } from "../../../../../shared/components/pageHeader/PageHeaderApi"
import {
    Breadcrumb,
    BreadcrumbLink,
    StandardBreadcrumbs,
} from "../../../../../shared/components/links/StandardBreadcrumbs"
import { StandardButton } from "../../../../../shared/components/buttons/StandardButton"
import { AllProductsTab } from "./AllProductsTab"
import { useApiQuery } from "../../../../../core/api/useApiQuery"
import { FetchPriceListsResponse, PriceListQuery, fetchPriceLists } from "../../priceList/priceListApi"
import { useMerchant } from "../../../../merchants/merchantHooks"
import { FetchProductsResponse, MerchantProductsQuery, fetchProducts } from "../productsApi"

const selectPriceLists = (data: FetchPriceListsResponse) => data?.priceLists || []
const selectHasNoPriceListProducts = (data: FetchProductsResponse) => data?.productGroups?.length > 0

export function Products() {
    const { l10n } = useAppLocalization()
    const navigate = useNavigate()
    const { merchantId } = useMerchant()

    const { data: priceLists = [], isLoading } = useApiQuery(fetchPriceLists, {
        queryName: PriceListQuery.LIST,
        dependencies: {
            merchantId,
        },
        select: selectPriceLists,
    })

    const { data: hasNoPriceListProducts = false } = useApiQuery(fetchProducts, {
        queryName: MerchantProductsQuery.LIST,
        dependencies: { merchantId, priceListId: "NONE" as const },
        select: selectHasNoPriceListProducts,
    })

    const hasPriceLists = priceLists.length > 0

    const tabs = useMemo(
        () => [
            ...priceLists.map((priceList) => ({
                label: priceList.name,
                component: <AllProductsTab priceListId={priceList.id} />,
            })),
            ...(hasNoPriceListProducts
                ? [
                      {
                          label: l10n.getString("products-no-price-list-tab"),
                          component: <AllProductsTab priceListId="NONE" />,
                      },
                  ]
                : []),
        ],
        [priceLists, hasNoPriceListProducts, l10n],
    )

    return (
        <>
            <LocalizedStrict id="products-page-header" attrs={{ title: true }}>
                <PageHeader
                    title="Products"
                    page={Page.MERCHANT_PRODUCTS}
                    rightElement={
                        hasPriceLists ? (
                            <LocalizedStrict id="products-create-product-button">
                                <StandardButton
                                    onClick={() => {
                                        navigate(MERCHANT_ADMIN_PRODUCTS_CREATE_ROUTE)
                                    }}
                                >
                                    Create product
                                </StandardButton>
                            </LocalizedStrict>
                        ) : undefined
                    }
                >
                    <StandardBreadcrumbs>
                        <LocalizedStrict id="merchant-nav-item-home">
                            <BreadcrumbLink to={MERCHANT_ADMIN_ROUTE}>Home</BreadcrumbLink>
                        </LocalizedStrict>
                        <LocalizedStrict id="merchant-nav-item-products-header">
                            <Breadcrumb>Products</Breadcrumb>
                        </LocalizedStrict>
                        <LocalizedStrict id="merchant-nav-item-products">
                            <Breadcrumb>Products</Breadcrumb>
                        </LocalizedStrict>
                    </StandardBreadcrumbs>
                </PageHeader>
            </LocalizedStrict>

            {!isLoading && <StandardTabs tabs={tabs} />}
        </>
    )
}
